import * as Yup from "yup";
import i18next from "../../i18n";

/**
 * Values React Hook Form manages for the create-material form (Task 6, Carbon migration) — one
 * shape shared by both "kind"s the type toggle offers (`GOOD` / `SERVICE`). A `SERVICE`
 * submission only actually validates/sends `ean` + `globalMaterialName` (see
 * `newMaterialServiceValidationSchema` below and `NewMaterialForm.tsx`'s own payload-building),
 * but every field lives on one `useForm<NewMaterialFormValues>()` instance so switching "kind"
 * only changes which fields render and which schema resolves — not the whole form tree.
 */
export interface NewMaterialFormValues {
  ean: string;
  globalMaterialName: string;
  measurementUnit: string;
  customMeasurementUnit: string;
  deliveryFormat: string;
  deliveryPlace: string;
  brand: string;
  details: string;
  comments: string;
}

/**
 * Field-level rules, exported individually — not just the two composed schemas below — so a
 * later form on overlapping fields (`EditMaterialForm.tsx`, Task 7 in this same programme) can
 * compose its own schema out of these instead of re-typing each Yup rule and risking the two
 * forms drifting on wording. Each rule here is the exact behaviour the pre-Carbon
 * `utils/validations.ts#newMaterialValidationSchema`/`newServiceValidationSchema` enforced — see
 * `task-6-report.md`'s OLD→NEW table for the full audit against that pre-Carbon form.
 */
export const eanValidator = Yup.string().required(i18next.t("validations.ean"));

export const globalMaterialNameValidator = Yup.string().required(
  i18next.t("validations.globalMaterialName")
);

export const measurementUnitValidator = Yup.string().required(
  i18next.t("validations.selectMeasurementUnit")
);

/**
 * `customMeasurementUnit` is required ONLY when `measurementUnit` is the literal "Other
 * (specify)" option — exactly the pre-Carbon schema's own `Yup.string().when("measurementUnit",
 * {...})`. `is` compares against `i18next.t("measurementUnits.other")` evaluated once, at module
 * load — matching the pre-Carbon schema's own behaviour verbatim (that schema also read the
 * translation once, at its own module-load time, not per-validation-run) rather than "fixing" it
 * to re-read on every validation, which would be a behavioural change this task isn't scoped to
 * make.
 */
export const customMeasurementUnitValidator = Yup.string().when(
  "measurementUnit",
  {
    is: i18next.t("measurementUnits.other"),
    then: (schema: any) =>
      schema.required(i18next.t("validations.customMeasurementUnit")),
    otherwise: (schema: any) => schema.notRequired(),
  }
);

export const deliveryPlaceValidator = Yup.string().required(
  i18next.t("validations.selectDeliveryPlace")
);

export const detailsValidator = Yup.string().max(
  255,
  i18next.t("validations.details255")
);

/**
 * `deliveryFormat`/`brand`/`comments` carry no rule here, same as the pre-Carbon schema: neither
 * `newMaterialValidationSchema` nor the pre-Carbon component enforced anything on them beyond
 * "optional free text" (`brand`/`comments` were rendered with the old `InputField`'s `isOptional`
 * flag — a label suffix only, never a validation rule).
 *
 * `specifications` (the pre-Carbon schema's `Yup.string()` on the file-input field) is
 * deliberately NOT reproduced: it was a no-op there too — `Yup.string()` with no `.required()`/
 * `.test()` accepts `undefined` and rejects nothing a real `<input type="file">`'s value could
 * ever produce, so it enforced no rule to preserve. `MaterialFileUpload.tsx` owns the upload
 * itself; nothing needs a matching placeholder key in this schema.
 */
export const newMaterialGoodsValidationSchema = Yup.object({
  ean: eanValidator,
  globalMaterialName: globalMaterialNameValidator,
  measurementUnit: measurementUnitValidator,
  customMeasurementUnit: customMeasurementUnitValidator,
  deliveryPlace: deliveryPlaceValidator,
  details: detailsValidator,
  deliveryFormat: Yup.string(),
  brand: Yup.string(),
  comments: Yup.string(),
});

/**
 * The "Services" kind strips every Goods-only field from the payload (see
 * `NewMaterialForm.tsx`'s own service payload builder) exactly like the pre-Carbon
 * `newServiceValidationSchema` did — only `ean`/`globalMaterialName` are real, validated fields
 * for a service.
 */
export const newMaterialServiceValidationSchema = Yup.object({
  ean: eanValidator,
  globalMaterialName: globalMaterialNameValidator,
});
